var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import ArrowLeft from '@mui/icons-material/ArrowLeft';
import ArrowRight from '@mui/icons-material/ArrowRight';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import useResizeObserver from 'use-resize-observer';
import { useCardLayout } from '../useCardLayout';
const StyledRoot = styled(Box) `
    position: relative;
    width: 100%;
    height: 100%;
`;
const StyledPageButton = styled(IconButton) `
    position: absolute;
    z-index: 100;
    top: 50%;
    transform: translateY(-50%);
    background-color: ${({ theme }) => theme.palette.primary.light};
    color: ${({ theme }) => theme.palette.primary.main};

    &:hover {
        background-color: ${({ theme }) => theme.palette.primary.dark};
        color: ${({ theme }) => theme.palette.primary.light};
    }
`;
const StyledLeftButton = styled(StyledPageButton) `
    left: 0;
`;
const StyledRightButton = styled(StyledPageButton) `
    right: 0;
`;
export const GalleryView = (props) => {
    const { cardAspectRatio = 16 / 9, focusFirstItem = false, hidePagination, maxItemsPerPage, minCardWidth, nextButtonProps, padding = 4, previousButtonProps, totalItemCount, children, onStartIndexUpdate, onCardsUpdated, startIndex: propsStartIndex, uniqueIds } = props, boxProps = __rest(props, ["cardAspectRatio", "focusFirstItem", "hidePagination", "maxItemsPerPage", "minCardWidth", "nextButtonProps", "padding", "previousButtonProps", "totalItemCount", "children", "onStartIndexUpdate", "onCardsUpdated", "startIndex", "uniqueIds"]);
    const { ref, width: containerWidth = 1, height: containerHeight = 1 } = useResizeObserver();
    const [startIndexInternal, setStartIndexInternal] = useState(0);
    const startIndex = propsStartIndex !== null && propsStartIndex !== void 0 ? propsStartIndex : startIndexInternal;
    const actualCardCount = Math.max(Math.min(maxItemsPerPage, totalItemCount - startIndex), 0);
    const itemsPerPage = Math.min(maxItemsPerPage, totalItemCount);
    const options = {
        actualCardCount,
        cardAspectRatio,
        cardCountLimit: itemsPerPage,
        focusAlign: 'top',
        focusedMode: focusFirstItem,
        minCardWidth: minCardWidth,
        padding
    };
    const { cards, maxPossibleCards, isFirstCardFocused, allZoomSizes, currentZoomSizeIndex } = useCardLayout(containerWidth, containerHeight, options);
    const displayLeftButton = startIndex > 0;
    const displayRightButton = startIndex + maxPossibleCards < totalItemCount;
    const paginationButtonDisplayed = displayLeftButton || displayRightButton;
    const updateStartIndex = useCallback((index) => {
        if (propsStartIndex !== undefined) {
            onStartIndexUpdate === null || onStartIndexUpdate === void 0 ? void 0 : onStartIndexUpdate(index);
            return;
        }
        setStartIndexInternal(index);
    }, [onStartIndexUpdate, propsStartIndex]);
    const onPreviousClick = useCallback(() => {
        const newStartIndex = Math.max(startIndex - maxPossibleCards, 0);
        updateStartIndex(newStartIndex);
    }, [maxPossibleCards, startIndex, updateStartIndex]);
    const onNextClick = useCallback(() => {
        const newStartIndex = startIndex + maxPossibleCards;
        updateStartIndex(newStartIndex);
    }, [maxPossibleCards, startIndex, updateStartIndex]);
    const cardIndexToKey = useCallback((index) => {
        const withOffset = index + startIndex;
        if (uniqueIds) {
            if (withOffset >= uniqueIds.length) {
                throw new Error('An array of uniqueIds was provided, however its length seems to be lower than totalItemCount.');
            }
            return uniqueIds[withOffset];
        }
        return withOffset;
    }, [startIndex, uniqueIds]);
    // when the last page becomes empty due to change of number of total cards, move back to a full page
    useEffect(() => {
        if (startIndex >= totalItemCount) {
            updateStartIndex(Math.max(totalItemCount - maxPossibleCards, 0));
        }
    }, [totalItemCount, maxPossibleCards, startIndex, updateStartIndex]);
    useEffect(() => {
        setTimeout(() => {
            const keys = cards.map((_, i) => cardIndexToKey(i));
            onCardsUpdated === null || onCardsUpdated === void 0 ? void 0 : onCardsUpdated(cards, keys);
        });
    }, [cardIndexToKey, cards, onCardsUpdated]);
    return (_jsxs(StyledRoot, Object.assign({}, boxProps, { children: [_jsx(Box, { sx: {
                    position: 'absolute',
                    width: paginationButtonDisplayed ? 'calc(100% - 88px)' : '100%',
                    height: '100%',
                    left: '50%',
                    transform: 'translate(-50%)'
                }, ref: ref, children: cards.map((card, i) => (
                // Using div instead of Box so MUI is not complaining about missing sx props being created multiple times
                _jsx("div", { style: {
                        width: card.width,
                        height: card.height,
                        left: card.start,
                        top: card.top,
                        position: 'absolute'
                    }, className: 'ClassUiGalleryView-card', children: children({
                        cardKey: cardIndexToKey(i),
                        focused: isFirstCardFocused && i === 0,
                        position: card.position,
                        cols: allZoomSizes[currentZoomSizeIndex !== null && currentZoomSizeIndex !== void 0 ? currentZoomSizeIndex : 0].cols,
                        rows: allZoomSizes[currentZoomSizeIndex !== null && currentZoomSizeIndex !== void 0 ? currentZoomSizeIndex : 0].rows
                    }) }, cardIndexToKey(i)))) }), !hidePagination && displayLeftButton && (_jsx(StyledLeftButton, Object.assign({ "data-testid": 'pagination-previous', onClick: onPreviousClick }, previousButtonProps, { children: _jsx(ArrowLeft, {}) }))), !hidePagination && displayRightButton && (_jsx(StyledRightButton, Object.assign({ "data-testid": 'pagination-next', onClick: onNextClick }, nextButtonProps, { children: _jsx(ArrowRight, {}) })))] })));
};
